import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { recordEditorSignature } from "./recordEditorSignature";

// Immutable document snapshots keep structural sharing; preview caches and
// navigation do not become editing steps. Text typing is grouped per field.
export default function useEditorHistory(value, { scope, enabled, blocked, onRestore }) {
  const state = useRef({ scope, value, signature: recordEditorSignature(value), past: [], future: [] });
  const resetNext = useRef(false);
  const restore = useRef(onRestore);
  restore.current = onRestore;
  const [, refresh] = useState(0);
  useLayoutEffect(() => {
    const history = state.current;
    const signature = recordEditorSignature(value);
    if (resetNext.current || history.scope !== scope) {
      resetNext.current = false;
      state.current = { scope, value, signature, past: [], future: [] };
      refresh((n) => n + 1);
      return;
    }
    if (signature === history.signature) { history.value = value; return; }
    const element = document.activeElement;
    const field = element?.matches('textarea, input:not([type="checkbox"]):not([type="radio"]):not([type="range"])') ? element : null;
    const now = Date.now();
    if (!field || field !== history.field || now - history.time > 500 || history.future.length) {
      history.past = [...history.past.slice(-49), history.value];
    }
    history.future = [];
    Object.assign(history, { value, signature, field, time: now });
    refresh((n) => n + 1);
  });
  const move = useCallback((direction) => {
    if (!enabled || blocked) return false;
    const history = state.current;
    const from = direction === "undo" ? "past" : "future";
    const to = direction === "undo" ? "future" : "past";
    if (!history[from].length) return false;
    const next = history[from][history[from].length - 1];
    history[from] = history[from].slice(0, -1);
    history[to] = [...history[to].slice(-49), history.value];
    history.field = null;
    history.value = next;
    history.signature = recordEditorSignature(next);
    // The restored snapshot is already the history present; the layout effect
    // sees the same signature and must not record the restore as a fresh edit.
    restore.current(next);
    refresh((n) => n + 1);
    return true;
  }, [enabled, blocked]);
  const undo = useCallback(() => move("undo"), [move]);
  const redo = useCallback(() => move("redo"), [move]);
  useLayoutEffect(() => {
    if (!enabled || blocked) return undefined;
    const keydown = (event) => {
      if (event.defaultPrevented || event.isComposing || event.altKey || !(event.ctrlKey || event.metaKey)) return;
      // Dialog editors (including Python and question-bank edits) own their
      // own input history, rather than changing the handout behind the dialog.
      if (event.target?.closest?.('[role="dialog"], .monaco-editor, .cm-editor')) return;
      const key = event.key.toLowerCase();
      const action = key === "y" || (key === "z" && event.shiftKey) ? redo : key === "z" ? undo : null;
      if (!action) return;
      event.preventDefault();
      event.stopPropagation();
      action();
    };
    window.addEventListener("keydown", keydown, true);
    return () => window.removeEventListener("keydown", keydown, true);
  }, [enabled, blocked, undo, redo]);
  const reset = useCallback(() => { resetNext.current = true; }, []);
  return { undo, redo, reset, canUndo: !blocked && state.current.past.length > 0, canRedo: !blocked && state.current.future.length > 0 };
}
